import React, { useState } from 'react';
import { toast } from '../utils/toast';
import { geminiApi } from '../services/geminiApi';
import { QuickStart } from '../components/QuickStart';
import { VoiceRecorder } from '../components/VoiceRecorder';
import { BetaBadge, BetaMessage } from '../components/BetaDisclaimer';
import DeploymentOptions from '../components/DeploymentOptions';

export function CourseOutlineCreator() {
  const [contentInput, setContentInput] = useState('');
  const [courseLevel, setCourseLevel] = useState('undergraduate');
  const [courseDuration, setCourseDuration] = useState('semester');
  const [courseType, setCourseType] = useState('core');
  const [deliveryMethod, setDeliveryMethod] = useState('inperson');
  const [generatedContent, setGeneratedContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const courseLevelOptions = [
    { value: 'undergraduate', label: 'Undergraduate' },
    { value: 'graduate', label: 'Graduate' },
    { value: 'professional', label: 'Professional Development' },
    { value: 'certificate', label: 'Certificate Program' }
  ];

  const courseDurationOptions = [
    { value: 'semester', label: 'Full Semester' },
    { value: 'quarter', label: 'Quarter' },
    { value: 'intensive', label: 'Intensive (4-8 weeks)' },
    { value: 'workshop', label: 'Workshop Series' }
  ];

  const courseTypeOptions = [
    { value: 'core', label: 'Core Course' },
    { value: 'elective', label: 'Elective' },
    { value: 'seminar', label: 'Seminar' },
    { value: 'lab', label: 'Laboratory' },
    { value: 'practicum', label: 'Practicum' }
  ];

  const deliveryMethodOptions = [
    { value: 'inperson', label: 'In-Person' },
    { value: 'online', label: 'Online' },
    { value: 'hybrid', label: 'Hybrid' },
    { value: 'flexible', label: 'Flexible' }
  ];

  const quickStartGuides = [
    {
      title: "Course Structure",
      description: "Define the overall structure and flow of your course content.",
      keywords: ["Modules", "Units", "Progression"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      )
    },
    {
      title: "Learning Outcomes",
      description: "Set clear, measurable learning outcomes for your course.",
      keywords: ["Objectives", "Goals", "Competencies"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      title: "Assessment Plan",
      description: "Design comprehensive assessment strategies for your course.",
      keywords: ["Evaluation", "Grading", "Feedback"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      )
    }
  ];

  const handleTranscriptionComplete = (transcribedText) => {
    setContentInput(transcribedText);
  };

  const generateContent = async () => {
    if (!contentInput.trim()) {
      toast.error('Please provide course details');
      return;
    }

    setIsGenerating(true);
    try {
      const prompt = `Create a detailed course outline for: ${contentInput}
Subject Area: ${courseType}
Duration: ${courseDuration}
Level: ${courseLevel}
Delivery Method: ${deliveryMethod}

Please include:
- Course objectives
- Weekly topics
- Learning outcomes
- Assessment methods
- Required materials

Format the response as a structured course outline with clear sections and bullet points.`;

      const response = await geminiApi.generateText(prompt);

      if (response.error) {
        throw new Error(response.error);
      } else {
        // Check for both content and message properties
        const generatedText = response.content || response.message;
        if (generatedText) {
          setGeneratedContent(generatedText);
          toast.success('Course outline generated successfully!');
        } else {
          throw new Error('No content received from the API');
        }
      }
    } catch (error) {
      console.error('Error generating course outline:', error);
      toast.error(error.message || 'Failed to generate course outline');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Modern Hero Header */}
      <div className="relative mb-8 p-8 rounded-3xl bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 shadow-2xl overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:24px_24px]"></div>
          <div className="absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-400/20 to-transparent"></div>
          <div className="absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-transparent via-blue-400/20 to-transparent"></div>
          
          <div className="absolute -left-48 -top-48 h-96 w-96">
            <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-blue-500/40 via-blue-300/10 to-blue-400/40 blur-xl opacity-30"></div>
          </div>
          <div className="absolute -right-48 -bottom-48 h-96 w-96">
            <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-blue-400/40 via-blue-300/10 to-blue-500/40 blur-xl opacity-30"></div>
          </div>
        </div>

        {/* Content */}
        <div className="relative">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-4">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-blue-800/50 border border-blue-700/50 backdrop-blur-sm">
                <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></div>
                <span className="text-sm text-blue-100">AI-Powered Curriculum</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white flex items-center">
                Course Outline Creator
                <BetaBadge className="ml-3" />
              </h1>
              <p className="text-lg text-blue-200 max-w-xl">
                Design comprehensive course outlines with clear learning objectives
              </p>
              <BetaMessage />
              <div className="flex items-center gap-4 pt-2">
                <div className="flex items-center space-x-2 text-sm text-blue-200">
                  <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Smart Templates</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-blue-200">
                  <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Instant Refinements</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .bg-gradient-conic {
          background-image: conic-gradient(var(--tw-gradient-stops));
        }
      `}</style>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
        <div className="p-6">
          {/* Target Audience Banner */}
          <div className="mb-8 p-4 bg-blue-50 rounded-lg">
            <h3 className="text-lg font-medium text-blue-900 mb-2">Who is this tool for?</h3>
            <ul className="list-disc list-inside text-blue-700 space-y-1">
              <li>Course Instructors - Create detailed course outlines</li>
              <li>Academic Coordinators - Design structured learning paths</li>
              <li>Educational Administrators - Plan curriculum development</li>
            </ul>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            {/* Left column - Content Generation */}
            <div className="space-y-8">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Course Settings</h2>
                  <div className="space-y-6">
                    {/* Voice Recorder */}
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Record Course Details
                      </label>
                      <VoiceRecorder 
                        onTranscriptionComplete={handleTranscriptionComplete}
                        placeholder="Click to start recording course details..."
                      />
                    </div>

                    {/* Content Input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Course Overview
                      </label>
                      <textarea
                        value={contentInput}
                        onChange={(e) => setContentInput(e.target.value)}
                        rows="4"
                        placeholder="Record audio or type your course details..."
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    {/* Course Level and Duration */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Course Level
                        </label>
                        <div className="grid grid-cols-1 gap-2">
                          {courseLevelOptions.map((option) => (
                            <button
                              key={option.value}
                              onClick={() => setCourseLevel(option.value)}
                              className={`flex items-center justify-center px-4 py-2 rounded-lg border ${
                                courseLevel === option.value
                                  ? 'bg-blue-50 border-blue-500 text-blue-700 shadow-sm'
                                  : 'border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Course Duration
                        </label>
                        <div className="grid grid-cols-1 gap-2">
                          {courseDurationOptions.map((option) => (
                            <button
                              key={option.value}
                              onClick={() => setCourseDuration(option.value)}
                              className={`flex items-center justify-center px-4 py-2 rounded-lg border ${
                                courseDuration === option.value
                                  ? 'bg-blue-50 border-blue-500 text-blue-700 shadow-sm'
                                  : 'border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Course Type and Delivery Method */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Course Type
                        </label>
                        <div className="grid grid-cols-1 gap-2">
                          {courseTypeOptions.map((option) => (
                            <button
                              key={option.value}
                              onClick={() => setCourseType(option.value)}
                              className={`flex items-center justify-center px-4 py-2 rounded-lg border ${
                                courseType === option.value
                                  ? 'bg-blue-50 border-blue-500 text-blue-700 shadow-sm'
                                  : 'border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Delivery Method
                        </label>
                        <div className="grid grid-cols-1 gap-2">
                          {deliveryMethodOptions.map((option) => (
                            <button
                              key={option.value}
                              onClick={() => setDeliveryMethod(option.value)}
                              className={`flex items-center justify-center px-4 py-2 rounded-lg border ${
                                deliveryMethod === option.value
                                  ? 'bg-blue-50 border-blue-500 text-blue-700 shadow-sm'
                                  : 'border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={generateContent}
                      disabled={isGenerating}
                      className={`w-full px-6 py-3 rounded-lg font-medium text-white transition-all duration-200 ${
                        isGenerating
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700'
                      }`}
                    >
                      {isGenerating ? (
                        <div className="flex items-center justify-center">
                          <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Creating Course Outline...
                        </div>
                      ) : (
                        'Generate Course Outline'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right column - Generated Content */}
            <div className="space-y-8">
              {generatedContent ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-xl font-semibold text-gray-900">Generated Course Outline</h3>
                      <button
                        onClick={() => copyToClipboard(generatedContent)}
                        className="inline-flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copy to Clipboard
                      </button>
                    </div>
                    <div className="prose max-w-none">
                      <div className="whitespace-pre-wrap bg-gray-50 rounded-lg p-6 border border-gray-200">
                        {generatedContent}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-6">
                    <div className="text-center py-12">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No course outline generated yet</h3>
                      <p className="mt-1 text-sm text-gray-500">Fill in the course details and click generate to create your outline.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <QuickStart guides={quickStartGuides} />
      <DeploymentOptions appId="course-outline-creator" position="none" />
    </div>
  );
}

export default CourseOutlineCreator; 